import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { Alert, Card, Empty, Skeleton, Space, Tag, Typography } from 'antd';
import type { TeacherPayslipRow } from '@velora/contracts';
import { productApi } from '../../api';

/**
 * Teacher — Payslips. M-09: only surfaces locked/paid runs. Draft never leaks.
 */
export function TeacherPayslipsPage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const q = useQuery({
    queryKey: ['teacher-payslips', institution],
    queryFn: () => productApi.getTeacherPayslips(institution),
    enabled: !!institution,
  });
  if (q.isLoading || !q.data) return <Skeleton active paragraph={{ rows: 6 }} />;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div>
        <Typography.Title level={3} style={{ margin: 0 }}>Payslips</Typography.Title>
        <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
          {q.data.rows.length} slip{q.data.rows.length === 1 ? '' : 's'}
        </Typography.Text>
      </div>

      {!q.data.teacher ? (
        <Alert type="warning" showIcon message="Your login email isn't linked to a staff record." />
      ) : q.data.rows.length === 0 ? (
        <Empty description="No payslips yet. Locked payroll runs will appear here." style={{ padding: 20 }} />
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {q.data.rows.map((r) => <PayslipCard key={r.run_id} r={r} />)}
        </div>
      )}
    </Space>
  );
}

function PayslipCard({ r }: { r: TeacherPayslipRow }) {
  return (
    <Card size="small">
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
        <b style={{ fontSize: 15 }}>{r.month}</b>
        <Tag color={r.status === 'paid' ? 'success' : 'processing'}>{r.status}</Tag>
        <div style={{ flex: 1 }} />
        <div style={{ fontSize: 18, fontWeight: 700 }}>{formatRupees(r.net_paise)}</div>
      </div>
      <div style={{ fontSize: 11.5, color: '#8a94a6' }}>
        gross {formatRupees(r.gross_paise)} · deductions {formatRupees(r.deductions_paise)}
        {r.paid_at && <> · paid {new Date(r.paid_at).toLocaleDateString()}</>}
      </div>
      <table style={{ width: '100%', marginTop: 10, fontSize: 12.5 }}>
        <tbody>
          <ComponentRow label="Basic" value={r.components.basic} />
          <ComponentRow label="HRA" value={r.components.hra} />
          <ComponentRow label="DA" value={r.components.da} />
          <ComponentRow label="Other allowance" value={r.components.other_allowance} />
          <ComponentRow label="PF" value={-r.components.pf} />
          <ComponentRow label="ESI" value={-r.components.esi} />
          <ComponentRow label="Professional tax" value={-r.components.professional_tax} />
          <ComponentRow label="Other deduction" value={-r.components.other_deduction} />
        </tbody>
      </table>
    </Card>
  );
}

function ComponentRow({ label, value }: { label: string; value: number }) {
  if (value === 0) return null;
  return (
    <tr>
      <td style={{ color: '#5b6478', padding: '2px 0' }}>{label}</td>
      <td style={{ textAlign: 'right', color: value < 0 ? '#a24040' : '#14213d' }}>
        {formatRupees(value)}
      </td>
    </tr>
  );
}

function formatRupees(paise: number): string {
  const rupees = paise / 100;
  const sign = rupees < 0 ? '-' : '';
  return `${sign}₹${Math.abs(rupees).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
